import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import TokenRegistryForm from './TokenRegistryForm'
import MiniSwitch from '../formUtils/MiniSwitch'
import CustomConfirmationModal from '../CustomConfirmationModal'
import Address from '../general/Address'
import FormModal from '../FormModal'
import Price from './Price'
import Network from '../general/Network'
import { useTokenRegistry } from '../../hooks/useTokenRegistry'
import { useApi } from '../../hooks/useApi'


interface TokenRegistryData {
  _id: string
  address: string
  chainId: number
  decimals: number
  spamCounter: number
  priority: number
  isNativeToken: boolean
  enabled: boolean
  isWrappedNativeToken: boolean
  name: string
  symbol: string
}

const TokenRegistry: React.FC = () => {
  const { callApi } = useApi()
  const [symbolFilter, setSymbolFilter] = useState('')
  const [addressFilter, setAddressFilter] = useState('')
  const [chainIdFilter, setChainIdFilter] = useState<number | ''>('')
  const [isNativeFilter, setIsNativeFilter] = useState<boolean | null>(null)
  const [enabledFilter, setEnabledFilter] = useState<boolean | null>(null)
  const [isWrappedNativeFilter, setIsWrappedNativeFilter] = useState<boolean | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [customModalOpen, setCustomModalOpen] = useState(false)
  const [addNew, setAddNew] = useState(false)
  const [deleteParams, setDeleteParams] = useState<any>(null)
  const [editedProps, setEditedProps] = useState<TokenRegistryData | null>(null)
  const [editIndex, setEditIndex] = useState<number | null>(null)

  const filters = { chainIdFilter, symbolFilter, addressFilter, isNativeFilter, enabledFilter, isWrappedNativeFilter }
  const { data: tokenRegistryData, isLoading, refetch } = useTokenRegistry(currentPage, filters)

  const parseBool = (val: string): boolean | null =>
    val === 'yes' ? true : val === 'no' ? false : null

  const handleEdit = (item: TokenRegistryData, index: number) => {
    setEditIndex(index)
    setEditedProps(item)
  }

  const handleSaveEdit = async () => {
    try {
      await callApi(
        `/token-registry/tokens/${editedProps!.chainId}/${editedProps!.address}`,
        { ...editedProps, decimals: parseInt(String(editedProps!.decimals)) },
        'PUT'
      )
      refetch(currentPage)
    } catch (error) {
      console.error('Error updating token:', error)
    }
    setEditIndex(null)
  }

  const ep = editedProps

  return (
    <Wrap>

      <Header>
        <AddBtn onClick={() => setAddNew(!addNew)}>
          {addNew ? 'Hide form' : '+ Add token'}
        </AddBtn>
      </Header>

      {addNew && (
        <FormModal onConfirm={() => { refetch(1); setAddNew(false) }} onCancel={() => setAddNew(false)}>
          <TokenRegistryForm onSuccess={() => { refetch(1); setAddNew(false) }} />
        </FormModal>
      )}

      <FiltersBar>
        <FilterGroup>
          <FilterLabel>Symbol</FilterLabel>
          <FilterInput type="text" placeholder="Filter..." value={symbolFilter} onChange={(e) => setSymbolFilter(e.target.value)} />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Address</FilterLabel>
          <FilterInput type="text" placeholder="Filter..." value={addressFilter} onChange={(e) => setAddressFilter(e.target.value)} />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Chain</FilterLabel>
          <FilterInput type="number" placeholder="Chain ID" value={chainIdFilter} onChange={(e) => setChainIdFilter(parseInt(e.target.value))} />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Native</FilterLabel>
          <FilterSelect value={isNativeFilter === null ? 'all' : isNativeFilter ? 'yes' : 'no'} onChange={(e) => setIsNativeFilter(parseBool(e.target.value))}>
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </FilterSelect>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Enabled</FilterLabel>
          <FilterSelect value={enabledFilter === null ? 'all' : enabledFilter ? 'yes' : 'no'} onChange={(e) => setEnabledFilter(parseBool(e.target.value))}>
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </FilterSelect>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Wrapped</FilterLabel>
          <FilterSelect value={isWrappedNativeFilter === null ? 'all' : isWrappedNativeFilter ? 'yes' : 'no'} onChange={(e) => setIsWrappedNativeFilter(parseBool(e.target.value))}>
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </FilterSelect>
        </FilterGroup>
      </FiltersBar>

      {isLoading ? (
        <LoaderBar />
      ) : tokenRegistryData?.data && (
        <>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Address</Th>
                  <Th>Symbol</Th>
                  <Th>Name</Th>
                  <Th>Network</Th>
                  <Th>Decimals</Th>
                  <Th>Spam</Th>
                  <Th>Priority</Th>
                  <Th>Enabled</Th>
                  <Th>Native</Th>
                  <Th>Wrapped</Th>
                  <Th>Price</Th>
                  <Th></Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {Object.values(tokenRegistryData.data).map((item, index) => {
                  const isEditing = editIndex === index
                  return (
                    <tr key={index}>
                      <Td>
                        {isEditing ? (
                          <InlineInput type="text" style={{ width: 200 }} value={ep?.address ?? ''} onChange={(e) => setEditedProps({ ...ep!, address: e.target.value })} />
                        ) : <Address address={item.address} short chainId={item.chainId} showIdentity={false} />}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <InlineInput type="text" style={{ width: 80 }} value={ep?.symbol ?? ''} onChange={(e) => setEditedProps({ ...ep!, symbol: e.target.value })} />
                        ) : <Mono>{item.symbol}</Mono>}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <InlineInput type="text" style={{ width: 120 }} value={ep?.name ?? ''} onChange={(e) => setEditedProps({ ...ep!, name: e.target.value })} />
                        ) : item.name}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <InlineInput type="text" style={{ width: 70 }} value={ep?.chainId ?? ''} onChange={(e) => setEditedProps({ ...ep!, chainId: parseInt(e.target.value) })} />
                        ) : <Network network={item.chainId} />}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <InlineInput type="number" style={{ width: 60 }} value={ep?.decimals ?? 0} onChange={(e) => setEditedProps({ ...ep!, decimals: parseInt(e.target.value) })} />
                        ) : <Mono>{item.decimals}</Mono>}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <InlineInput type="number" style={{ width: 60 }} value={ep?.spamCounter ?? 0} onChange={(e) => setEditedProps({ ...ep!, spamCounter: parseInt(e.target.value) })} />
                        ) : <Mono>{item.spamCounter}</Mono>}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <InlineInput type="number" style={{ width: 60 }} value={ep?.priority ?? 0} onChange={(e) => setEditedProps({ ...ep!, priority: parseInt(e.target.value) })} />
                        ) : <Mono>{item.priority}</Mono>}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <MiniSwitch ison={ep!.enabled} onToggle={() => setEditedProps({ ...ep!, enabled: !ep!.enabled })} />
                        ) : <StatusDot active={item.enabled} />}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <MiniSwitch ison={ep!.isNativeToken} onToggle={() => setEditedProps({ ...ep!, isNativeToken: !ep!.isNativeToken })} />
                        ) : <StatusDot active={item.isNativeToken} />}
                      </Td>
                      <Td>
                        {isEditing ? (
                          <MiniSwitch ison={ep!.isWrappedNativeToken} onToggle={() => setEditedProps({ ...ep!, isWrappedNativeToken: !ep!.isWrappedNativeToken })} />
                        ) : <StatusDot active={item.isWrappedNativeToken} />}
                      </Td>
                      <Td><Price item={item} /></Td>
                      <Td>
                        {isEditing ? (
                          <BtnGroup>
                            <SaveBtn onClick={handleSaveEdit}>Save</SaveBtn>
                            <GhostBtn onClick={() => setEditIndex(null)}>Cancel</GhostBtn>
                          </BtnGroup>
                        ) : (
                          <EditBtn onClick={() => handleEdit(item, index)}>Edit</EditBtn>
                        )}
                      </Td>
                      <Td>
                        <DeleteBtn onClick={() => { setDeleteParams(item); setCustomModalOpen(true) }}>Delete</DeleteBtn>
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </TableWrap>

          <Pagination>
            <PageBtn onClick={() => { if (currentPage > 1) { setCurrentPage(p => p - 1); refetch() } }} disabled={currentPage === 1}>
              Previous
            </PageBtn>
            <PageInfo>Page {currentPage} of {tokenRegistryData.pages} — {tokenRegistryData.total} items</PageInfo>
            <PageBtn onClick={() => { if (currentPage < tokenRegistryData.pages) { setCurrentPage(p => p + 1); refetch() } }} disabled={currentPage === tokenRegistryData.pages}>
              Next
            </PageBtn>
          </Pagination>

          {customModalOpen && (
            <CustomConfirmationModal
              message="Are you sure you want to delete this token?"
              onConfirm={() => { setCustomModalOpen(false) }}
              onCancel={() => setCustomModalOpen(false)}
            />
          )}
        </>
      )}
    </Wrap>
  )
}

export default TokenRegistry

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`
const Header = styled.div`display: flex; justify-content: flex-end;`

const shimmer = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content: ''; display: block; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent); animation: ${shimmer} 1.4s ease infinite; }
`

const FiltersBar = styled.div`display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end;`
const FilterGroup = styled.div`display: flex; flex-direction: column; gap: 4px;`
const FilterLabel = styled.label`font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #a1a1a1;`
const FilterInput = styled.input`
  height: 28px; padding: 0 8px; background: #111; border: 1px solid rgba(255,255,255,0.07); border-radius: 5px;
  color: #a0a0a0; font-size: 12px; outline: none; width: 140px;
  &:focus { border-color: rgba(111,92,230,0.4); }
`
const FilterSelect = styled.select`
  height: 28px; padding: 0 8px; background: #111; border: 1px solid rgba(255,255,255,0.07); border-radius: 5px;
  color: #a0a0a0; font-size: 12px; outline: none; cursor: pointer;
  &:focus { border-color: rgba(111,92,230,0.4); }
`

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);`
const Td = styled.td`padding: 8px 10px; color: #a0a0a0; font-size: 13px; vertical-align: middle; border-bottom: 1px solid rgba(255,255,255,0.04); tr:last-child & { border-bottom: none; }`
const Mono = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 12px; color: #a0a0a0;`
const StatusDot = styled.span<{ active: boolean }>`display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: ${({ active }) => active ? '#33C2B0' : '#ff4444'};`

const InlineInput = styled.input`
  background: #0d0d0d; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;
  color: #ededed; font-size: 12px; padding: 0 6px; height: 26px; outline: none;
  &:focus { border-color: rgba(111,92,230,0.5); }
`

const BtnGroup = styled.div`display: flex; gap: 4px;`
const ActionBtn = styled.button`height: 26px; padding: 0 9px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 4px; color: #a0a0a0; font-size: 11px; cursor: pointer; transition: border-color 0.15s, color 0.15s;`
const EditBtn = styled(ActionBtn)`&:hover { border-color: rgba(111,92,230,0.4); color: #a89aff; }`
const SaveBtn = styled(ActionBtn)`&:hover { border-color: rgba(51,194,176,0.4); color: #33C2B0; }`
const GhostBtn = styled(ActionBtn)``
const DeleteBtn = styled(ActionBtn)`&:hover { border-color: rgba(255,68,68,0.4); color: #ff4444; }`

const AddBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px; color: #a89aff; font-size: 12px; font-weight: 500; cursor: pointer;
  transition: background 0.15s; &:hover { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
`

const Pagination = styled.div`display: flex; align-items: center; gap: 12px;`
const PageBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 5px; color: #a0a0a0; font-size: 12px; cursor: pointer;
  &:hover:not(:disabled) { border-color: rgba(255,255,255,0.15); color: #ededed; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`
const PageInfo = styled.span`font-size: 12px; color: #777;`
